import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { scannerApi } from '../../lib/api';
import { formatToLocalTimeOnly } from '../../lib/dateUtils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  QrCode,
  LogOut,
  CheckCircle,
  XCircle,
  AlertCircle,
  List,
  Camera,
  CameraOff,
  Volume2,
  VolumeX,
  User,
  Loader2,
  RefreshCw,
  Shirt,
  UserCircle,
  Tag,
  Ticket,
  Sparkles,
  UserPlus,
  ArrowLeft,
  Search,
  Mail,
  Phone,
  Plus,
  Trash2
} from 'lucide-react';
import jsQR from 'jsqr';

// Pre-generated confetti pieces (static to avoid re-renders)
const CONFETTI_PIECES = Array.from({ length: 50 }, (_, i) => ({
  id: i,
  left: `${(i * 2) % 100}%`,
  delay: `${(i % 5) * 0.1}s`,
  duration: `${1 + (i % 3) * 0.3}s`,
  color: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'][i % 8],
  rotation: `${(i * 37) % 360}deg`,
  shape: i % 2 === 0 ? '50%' : '0'
}));

// Confetti animation component
const Confetti = ({ active }) => {
  if (!active) return null;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
      {CONFETTI_PIECES.map((piece) => (
        <div
          key={piece.id}
          className="absolute w-3 h-3 animate-confetti"
          style={{
            left: piece.left,
            top: '-10px',
            backgroundColor: piece.color,
            animationDelay: piece.delay,
            animationDuration: piece.duration,
            transform: `rotate(${piece.rotation})`,
            borderRadius: piece.shape
          }}
        />
      ))}
      <style>{`
        @keyframes confetti {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .animate-confetti {
          animation: confetti linear forwards;
        }
      `}</style>
    </div>
  );
};

// Attendee detail row component
const DetailRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 py-2">
      <Icon className="h-5 w-5 opacity-70 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs opacity-70 uppercase tracking-wide">{label}</p>
        <p className="font-semibold text-lg truncate">{value}</p>
      </div>
    </div>
  );
};

const ScannerDashboard = () => {
  const { scanner, logoutScanner } = useAuth();
  const navigate = useNavigate();
  const loggingOutRef = useRef(false);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [stats, setStats] = useState(null);
  const [todayScans, setTodayScans] = useState([]);
  const [showScans, setShowScans] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [scanStatus, setScanStatus] = useState('idle'); // idle, scanning, detected
  const [debugInfo, setDebugInfo] = useState(''); // Debug info for mobile testing
  
  // Confirmation dialog states
  const [pendingQrData, setPendingQrData] = useState(null);
  const [pendingAttendee, setPendingAttendee] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [loadingAttendee, setLoadingAttendee] = useState(false);
  
  // On-Spot Registration states
  const [mode, setMode] = useState('scan'); // 'scan', 'onspot', or 'manual'
  const [onspotCode, setOnspotCode] = useState('');
  const [onspotBadge, setOnspotBadge] = useState(null);
  const [onspotCategories, setOnspotCategories] = useState([]);
  const [onspotDashboards, setOnspotDashboards] = useState([]);
  const [onspotLoading, setOnspotLoading] = useState(false);
  const [onspotSubmitting, setOnspotSubmitting] = useState(false);
  const [onspotForm, setOnspotForm] = useState({
    name: '',
    call_name: '',
    email: '',
    phone_number: '',
    phone_country_code: '+91',
    category_id: '',
    gender: '',
    tshirt_size: '',
    dietary_preference: '',
    club_name: '',
    custom_limits: {}
  });
  const [onspotSuccess, setOnspotSuccess] = useState(null);
  
  // Manual Check-in states
  const [manualSearchQuery, setManualSearchQuery] = useState('');
  const [manualSearchResults, setManualSearchResults] = useState([]);
  const [manualSearching, setManualSearching] = useState(false);
  const [manualSelectedAttendee, setManualSelectedAttendee] = useState(null);
  const [manualConfirming, setManualConfirming] = useState(false);

  // Vendor mode states
  const [vendorTab, setVendorTab] = useState('charge'); // 'charge', 'transactions', 'menu', 'summary'
  const [vendorScannedAttendee, setVendorScannedAttendee] = useState(null);
  const [vendorSelectedItems, setVendorSelectedItems] = useState({});
  const [vendorCharging, setVendorCharging] = useState(false);
  const [vendorChargeResult, setVendorChargeResult] = useState(null);
  const [vendorTransactions, setVendorTransactions] = useState([]);
  const [vendorSummary, setVendorSummary] = useState(null);
  const [vendorMenu, setVendorMenu] = useState([]);
  const [vendorScanning, setVendorScanning] = useState(false);
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const scanningRef = useRef(false);
  const processingRef = useRef(false);
  const frameCountRef = useRef(0);
  const vendorScanningRef = useRef(false);

  // Sound effects
  const playSound = (type) => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      if (type === 'success') {
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.3;
        oscillator.start();
        setTimeout(() => {
          oscillator.frequency.value = 1000;
          setTimeout(() => oscillator.stop(), 100);
        }, 100);
      } else if (type === 'error') {
        oscillator.frequency.value = 300;
        oscillator.type = 'square';
        gainNode.gain.value = 0.2;
        oscillator.start();
        setTimeout(() => oscillator.stop(), 300);
      } else if (type === 'warning') {
        oscillator.frequency.value = 500;
        oscillator.type = 'triangle';
        gainNode.gain.value = 0.25;
        oscillator.start();
        setTimeout(() => {
          oscillator.frequency.value = 400;
          setTimeout(() => oscillator.stop(), 150);
        }, 150);
      } else if (type === 'scan') {
        oscillator.frequency.value = 1200;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.2;
        oscillator.start();
        setTimeout(() => oscillator.stop(), 100);
      }
    } catch (e) {
      console.log('Sound not available');
    }
  };

  useEffect(() => {
    if (!scanner) {
      // Skip if an intentional logout already navigated to the event login page
      if (!loggingOutRef.current) {
        navigate('/scanner');
      }
      return;
    }
    fetchStats();
    fetchTodayScans();
    
    return () => {
      stopScanner();
    };
  }, [scanner, navigate]);

  const fetchStats = async () => {
    try {
      const response = await scannerApi.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchTodayScans = async () => {
    try {
      const response = await scannerApi.get('/dashboard/today-scans');
      setTodayScans(response.data || []);
    } catch (error) {
      console.error('Failed to fetch scans');
    }
  };

  const startScanner = async () => {
    setCameraError(null);
    setScanStatus('idle');
    setDebugInfo('Starting camera...');
    
    try {
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        setCameraError('Camera requires HTTPS connection.');
        toast.error('Camera requires HTTPS');
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError('Camera not supported on this browser.');
        toast.error('Camera not supported');
        return;
      }

      console.log('Requesting camera access...');
      setDebugInfo('Requesting camera permission...');
      
      // Try different constraint configurations for mobile compatibility
      const constraintOptions = [
        {
          video: {
            facingMode: { exact: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        },
        {
          video: {
            facingMode: 'environment',
            width: { ideal: 640 },
            height: { ideal: 480 }
          },
          audio: false
        },
        {
          video: { facingMode: 'environment' },
          audio: false
        },
        {
          video: true,
          audio: false
        }
      ];

      let stream = null;
      let lastError = null;

      for (let i = 0; i < constraintOptions.length; i++) {
        try {
          console.log(`Trying constraint set ${i + 1}:`, constraintOptions[i]);
          setDebugInfo(`Trying camera config ${i + 1}...`);
          stream = await navigator.mediaDevices.getUserMedia(constraintOptions[i]);
          console.log('Camera stream obtained with constraint set', i + 1);
          setDebugInfo(`Camera config ${i + 1} worked!`);
          break;
        } catch (e) {
          console.log(`Constraint set ${i + 1} failed:`, e.message);
          lastError = e;
        }
      }

      if (!stream) {
        throw lastError || new Error('Could not access camera');
      }
      
      streamRef.current = stream;
      
      // Get track settings for debugging
      const videoTrack = stream.getVideoTracks()[0];
      const settings = videoTrack.getSettings();
      console.log('Camera settings:', settings);
      setDebugInfo(`Camera: ${settings.width}x${settings.height}`);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.setAttribute('autoplay', 'true');
        videoRef.current.setAttribute('muted', 'true');
        
        // Wait for video to be ready with timeout
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Video load timeout'));
          }, 10000);

          videoRef.current.onloadedmetadata = () => {
            clearTimeout(timeout);
            console.log('Video metadata loaded:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
            setDebugInfo(`Video ready: ${videoRef.current.videoWidth}x${videoRef.current.videoHeight}`);
            resolve();
          };

          videoRef.current.onerror = (e) => {
            clearTimeout(timeout);
            reject(new Error('Video load error'));
          };
        });
        
        await videoRef.current.play();
        console.log('Video playing');
      }

      // Create canvas with proper size
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }
      
      setScanning(true);
      scanningRef.current = true;
      frameCountRef.current = 0;
      toast.success('Camera started! Point at QR code.');
      
      // Start scanning loop
      scanLoop();
      
    } catch (error) {
      console.error('Camera error:', error);
      let errorMessage = `Failed to start camera: ${error.message}`;
      
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Camera permission denied. Please allow camera in browser settings.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Camera is in use by another application.';
      }
      
      setCameraError(errorMessage);
      setDebugInfo(`Error: ${errorMessage}`);
      toast.error(errorMessage);
    }
  };

  const scanLoop = () => {
    if (!scanningRef.current) {
      return;
    }

    // Process frame
    const detected = processFrame();
    
    if (!detected && scanningRef.current) {
      // Use setTimeout for more consistent frame rate on mobile
      animationFrameRef.current = setTimeout(scanLoop, 100); // ~10fps for QR detection
    }
  };

  const processFrame = () => {
    if (processingRef.current) {
      return false;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!video || !canvas) {
      return false;
    }

    // Check video readiness
    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      return false;
    }

    // Ensure video has valid dimensions
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    
    if (!videoWidth || !videoHeight || videoWidth === 0 || videoHeight === 0) {
      return false;
    }

    // Update canvas size if needed
    if (canvas.width !== videoWidth || canvas.height !== videoHeight) {
      canvas.width = videoWidth;
      canvas.height = videoHeight;
      console.log('Canvas size set to:', canvas.width, 'x', canvas.height);
    }

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    
    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      frameCountRef.current++;
      
      // Log every 50 frames for debugging
      if (frameCountRef.current % 50 === 0) {
        console.log(`Frame ${frameCountRef.current}: Processing ${canvas.width}x${canvas.height} image`);
        setDebugInfo(`Scanning... (frame ${frameCountRef.current})`);
      }
      
      // Try to detect QR code with multiple strategies
      let code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert'
      });
      
      // If not found, try with inversion
      if (!code) {
        code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'onlyInvert'
        });
      }
      
      // If still not found, try attemptBoth
      if (!code) {
        code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'attemptBoth'
        });
      }
      
      if (code && code.data) {
        console.log('QR Code detected:', code.data);
        setDebugInfo(`QR Found: ${code.data.substring(0, 20)}...`);
        processingRef.current = true;
        setScanStatus('detected');
        playSound('scan');
        processQRCode(code.data);
        return true;
      }
    } catch (e) {
      console.error('Scan error:', e);
      setDebugInfo(`Scan error: ${e.message}`);
    }

    return false;
  };

  const processQRCode = async (rawQrData) => {
    // Strip URL prefix if present (new QR format wraps encrypted data in a URL)
    let qrData = rawQrData;
    const scanPathMatch = rawQrData.match(/\/scan\/(.+)$/);
    if (scanPathMatch) {
      qrData = decodeURIComponent(scanPathMatch[1]);
    }

    // Route to vendor handler if in vendor mode
    if (isVendor && vendorScanningRef.current) {
      setPendingQrData(qrData);
      handleVendorQrDetected(qrData);
      return;
    }

    setProcessing(true);
    setPendingQrData(qrData);
    setLoadingAttendee(true);

    try {
      const response = await scannerApi.post('/dashboard/scan/preview', { qr_data: qrData });
      setPendingAttendee(response.data);
      setShowConfirmDialog(true);
    } catch (error) {
      setPendingAttendee({
        valid: false,
        message: error.response?.data?.detail || 'Invalid QR code',
        qr_data: qrData
      });
      setShowConfirmDialog(true);
    } finally {
      setLoadingAttendee(false);
    }
  };

  const stopScanner = () => {
    console.log('Stopping scanner...');
    scanningRef.current = false;
    
    if (animationFrameRef.current) {
      clearTimeout(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setScanning(false);
    setScanStatus('idle');
    setCameraError(null);
    setDebugInfo('');
  };

  const resumeScanning = () => {
    processingRef.current = false;
    setProcessing(false);
    setScanStatus('scanning');
    if (scanningRef.current) {
      scanLoop();
    }
  };

  const handleConfirmScan = async () => {
    setShowConfirmDialog(false);
    setProcessing(true);
    
    try {
      const response = await scannerApi.post('/dashboard/scan', { qr_data: pendingQrData });
      const result = response.data;
      
      setLastScan(result);
      
      if (result.success) {
        playSound('success');
      } else if (result.status === 'duplicate') {
        playSound('warning');
      } else {
        playSound('error');
      }
      
      setShowResultDialog(true);
      fetchStats();
      fetchTodayScans();
      
    } catch (error) {
      playSound('error');
      setLastScan({
        success: false,
        message: 'Network error. Please try again.',
        status: 'invalid'
      });
      setShowResultDialog(true);
    }
  };

  const handleCancelScan = () => {
    setShowConfirmDialog(false);
    setPendingQrData(null);
    setPendingAttendee(null);
    resumeScanning();
  };

  const handleResultAcknowledged = () => {
    setShowResultDialog(false);
    setPendingQrData(null);
    setPendingAttendee(null);
    setLastScan(null);
    resumeScanning();
  };

  const handleLogout = () => {
    stopScanner();
    loggingOutRef.current = true;
    // Get the event slug before clearing scanner data (event_id works in the
    // scanner login route too, so use it when the event has no slug)
    const eventSlug = scanner?.event_url_slug || scanner?.event_id;
    // Navigate BEFORE clearing auth state so the !scanner guard effect
    // never gets a chance to bounce us to the generic /scanner page
    if (eventSlug) {
      navigate(`/scanner/${eventSlug}/login`);
    } else {
      navigate('/scanner');
    }
    logoutScanner();
  };

  const getResultColor = () => {
    if (!lastScan) return 'bg-gray-500';
    switch (lastScan.status) {
      case 'success': return 'bg-green-500';
      case 'duplicate': return 'bg-yellow-500';
      default: return 'bg-red-500';
    }
  };

  const getResultIcon = () => {
    if (!lastScan) return <QrCode className="h-12 w-12" />;
    switch (lastScan.status) {
      case 'success': return <CheckCircle className="h-12 w-12" />;
      case 'duplicate': return <AlertCircle className="h-12 w-12" />;
      default: return <XCircle className="h-12 w-12" />;
    }
  };

  // On-Spot Registration Functions
  const handleLookupBadge = async () => {
    if (!onspotCode.trim()) {
      toast.error('Enter a registration code');
      return;
    }
    
    setOnspotLoading(true);
    try {
      const response = await scannerApi.post('/dashboard/blank-badge/lookup', {
        code: onspotCode.trim().toUpperCase()
      });
      
      const { badge, categories, dashboards } = response.data;
      
      if (badge.status === 'activated') {
        toast.error(`Badge ${badge.registration_code} is already activated`);
        setOnspotBadge(null);
        return;
      }
      
      if (badge.status === 'voided') {
        toast.error(`Badge ${badge.registration_code} has been voided`);
        setOnspotBadge(null);
        return;
      }
      
      setOnspotBadge(badge);
      setOnspotCategories(categories || []);
      setOnspotDashboards(dashboards || []);
      
      // Reset form
      setOnspotForm({
        name: '',
        call_name: '',
        email: '',
        phone_number: '',
        phone_country_code: '+91',
        category_id: '',
        gender: '',
        tshirt_size: '',
        dietary_preference: '',
        club_name: '',
        custom_limits: {}
      });
      
      toast.success(`Found badge ${badge.registration_code}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Badge not found');
      setOnspotBadge(null);
    } finally {
      setOnspotLoading(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    const category = onspotCategories.find(c => c.category_id === categoryId);
    setOnspotForm(prev => ({
      ...prev,
      category_id: categoryId,
      custom_limits: category?.default_limits || {}
    }));
  };

  const handleOnspotSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!onspotForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!onspotForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!onspotForm.phone_number.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (!onspotForm.category_id) {
      toast.error('Category is required');
      return;
    }
    
    setOnspotSubmitting(true);
    try {
      const response = await scannerApi.post('/dashboard/blank-badge/activate', {
        registration_code: onspotBadge.registration_code,
        ...onspotForm
      });
      
      setOnspotSuccess(response.data);
      toast.success('Registration successful!');
      
      // Reset for next registration
      setOnspotBadge(null);
      setOnspotCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setOnspotSubmitting(false);
    }
  };

  const handleBackToModeSelect = () => {
    setMode('scan');
    setOnspotBadge(null);
    setOnspotCode('');
    setOnspotSuccess(null);
    // Reset manual check-in states
    setManualSearchQuery('');
    setManualSearchResults([]);
    setManualSelectedAttendee(null);
  };

  // Manual Check-in Functions
  const handleManualSearch = async () => {
    const query = manualSearchQuery.trim();
    if (!query) {
      toast.error('Enter a Ticket ID or Name to search');
      return;
    }
    
    setManualSearching(true);
    try {
      const response = await scannerApi.get(`/dashboard/attendees/search?q=${encodeURIComponent(query)}`);
      setManualSearchResults(response.data || []);
      if (response.data?.length === 0) {
        toast.info('No attendees found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
      setManualSearchResults([]);
    } finally {
      setManualSearching(false);
    }
  };

  const handleManualSelectAttendee = (attendee) => {
    setManualSelectedAttendee(attendee);
  };

  const handleManualConfirmCheckin = async () => {
    if (!manualSelectedAttendee) return;
    
    setManualConfirming(true);
    try {
      const response = await scannerApi.post('/dashboard/scan/manual', {
        attendee_id: manualSelectedAttendee.attendee_id
      });
      
      const result = response.data;
      setLastScan(result);
      
      if (result.success) {
        playSound('success');
      } else if (result.status === 'duplicate') {
        playSound('warning');
      } else {
        playSound('error');
      }
      
      setShowResultDialog(true);
      fetchStats();
      fetchTodayScans();
      
      // Reset manual check-in states
      setManualSelectedAttendee(null);
      setManualSearchQuery('');
      setManualSearchResults([]);
      
    } catch (error) {
      playSound('error');
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setManualConfirming(false);
    }
  };

  const handleManualCancelSelection = () => {
    setManualSelectedAttendee(null);
  };

  // ============== VENDOR MODE FUNCTIONS ==============

  const isVendor = scanner?.dashboard_type === 'vendor';

  const vendorItemsTotal = Object.values(vendorSelectedItems).reduce(
    (sum, item) => sum + item.price * item.qty, 0
  );

  const vendorItemsDescription = Object.entries(vendorSelectedItems)
    .filter(([_, item]) => item.qty > 0)
    .map(([name, item]) => `${item.qty}x ${name}`)
    .join(', ');

  const fetchVendorSummary = useCallback(async () => {
    try {
      const response = await scannerApi.get('/vendor/summary');
      setVendorSummary(response.data);
    } catch (error) { /* ignore */ }
  }, []);

  const fetchVendorTransactions = useCallback(async () => {
    try {
      const response = await scannerApi.get('/vendor/transactions', { params: { limit: 50 } });
      setVendorTransactions(response.data.transactions || []);
    } catch (error) { /* ignore */ }
  }, []);

  const fetchVendorMenu = useCallback(async () => {
    try {
      const response = await scannerApi.get('/vendor/menu');
      setVendorMenu(response.data.menu_items || []);
    } catch (error) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (isVendor) {
      fetchVendorSummary();
      fetchVendorTransactions();
      fetchVendorMenu();
    }
  }, [isVendor, fetchVendorSummary, fetchVendorTransactions, fetchVendorMenu]);

  const handleVendorQrDetected = async (qrData) => {
    if (vendorCharging) return;
    vendorScanningRef.current = false;
    setVendorScanning(false);
    stopScanner();
    try {
      const response = await scannerApi.post('/vendor/scan-preview', { qr_data: qrData });
      setVendorScannedAttendee(response.data);
      setVendorChargeResult(null);
      playSound('success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid QR code');
      playSound('error');
      // Restart scanner so user can retry
      vendorScanningRef.current = true;
      setVendorScanning(true);
      startScanner();
    }
  };

  const handleVendorCharge = async () => {
    if (!vendorScannedAttendee || vendorItemsTotal <= 0) return;
    if (vendorItemsTotal > 50000) {
      toast.error('Maximum charge amount is ₹50,000');
      return;
    }
    setVendorCharging(true);
    try {
      const response = await scannerApi.post('/vendor/charge', {
        qr_data: vendorScannedAttendee._lastQrData || pendingQrData,
        amount: vendorItemsTotal,
        items_description: vendorItemsDescription || undefined
      });
      setVendorChargeResult(response.data);
      setVendorSelectedItems({});
      playSound('success');
      fetchVendorSummary();
      fetchVendorTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Charge failed');
      playSound('error');
    } finally {
      setVendorCharging(false);
    }
  };

  const handleVendorScanStart = () => {
    setVendorScannedAttendee(null);
    setVendorChargeResult(null);
    setVendorSelectedItems({});
    processingRef.current = false;
    vendorScanningRef.current = true;
    setVendorScanning(true);
    startScanner();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  if (!scanner) {
    return null;
  }

  // ============== VENDOR MODE RENDER ==============
  if (isVendor) {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <h1 className="font-bold text-lg">{scanner.dashboard_name}</h1>
              <p className="text-sm text-gray-500">{scanner.event_name}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => setSoundEnabled(!soundEnabled)}>
                {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
          {/* Vendor Tabs */}
          <div className="flex border-t">
            {['charge', 'transactions', 'menu', 'summary'].map((tab) => (
              <button
                key={tab}
                onClick={() => setVendorTab(tab)}
                className={`flex-1 py-2.5 text-sm font-medium capitalize transition-colors ${
                  vendorTab === tab
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </header>

        <main className="p-4 pb-24">
          {/* Charge Tab */}
          {vendorTab === 'charge' && (
            <div className="space-y-4">
              {/* Today's stats bar */}
              <div className="flex gap-3">
                <Card className="flex-1">
                  <CardContent className="p-3 text-center">
                    <div className="text-xs text-gray-500">Today Sales</div>
                    <div className="text-lg font-bold text-green-600">
                      {formatCurrency(vendorSummary?.today?.today_sales)}
                    </div>
                  </CardContent>
                </Card>
                <Card className="flex-1">
                  <CardContent className="p-3 text-center">
                    <div className="text-xs text-gray-500">Transactions</div>
                    <div className="text-lg font-bold">
                      {vendorSummary?.today?.today_transactions || 0}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Scan / Charge Flow */}
              {!vendorScannedAttendee && !vendorScanning && (
                <Button
                  onClick={handleVendorScanStart}
                  className="w-full h-24 text-xl"
                  size="lg"
                >
                  <QrCode className="h-8 w-8 mr-3" /> Scan Badge
                </Button>
              )}

              {vendorScanning && (
                <Card>
                  <CardContent className="p-0">
                    <div className="relative aspect-square bg-black rounded-lg overflow-hidden">
                      <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                      <canvas ref={canvasRef} className="hidden" />
                      <div className="absolute inset-0 border-2 border-white/30 m-8 rounded-lg" />
                    </div>
                    <div className="p-3">
                      <Button variant="outline" className="w-full" onClick={() => { vendorScanningRef.current = false; setVendorScanning(false); stopScanner(); }}>
                        Cancel Scan
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Scanned Attendee Info */}
              {vendorScannedAttendee && !vendorChargeResult && (
                <Card>
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-bold">{vendorScannedAttendee.name}</h3>
                        {vendorScannedAttendee.category_name && (
                          <span
                            className="inline-block text-xs px-2 py-0.5 rounded-full mt-1 text-white"
                            style={{ backgroundColor: vendorScannedAttendee.category_color || '#666' }}
                          >
                            {vendorScannedAttendee.category_name}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">Balance</div>
                        <div className={`text-2xl font-bold ${
                          vendorScannedAttendee.wallet_balance > 100 ? 'text-green-600' : 'text-orange-500'
                        }`}>
                          {formatCurrency(vendorScannedAttendee.wallet_balance)}
                        </div>
                      </div>
                    </div>

                    {/* Menu items with quantity controls */}
                    {vendorMenu.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Select Items</p>
                        <div className="grid grid-cols-2 gap-2">
                          {vendorMenu.filter(m => m.active !== false).map((item, idx) => {
                            const qty = vendorSelectedItems[item.name]?.qty || 0;
                            return (
                              <button
                                key={idx}
                                onClick={() => {
                                  if (qty === 0) {
                                    setVendorSelectedItems(prev => ({
                                      ...prev,
                                      [item.name]: { price: item.price, qty: 1 }
                                    }));
                                  }
                                }}
                                className={`p-3 rounded-lg border text-left transition-colors relative ${
                                  qty > 0
                                    ? 'border-primary bg-primary/5'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <div className="font-medium text-sm">{item.name}</div>
                                <div className="text-primary font-bold">{formatCurrency(item.price)}</div>
                                {qty > 0 && (
                                  <div className="flex items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setVendorSelectedItems(prev => {
                                          const next = { ...prev };
                                          if (next[item.name].qty <= 1) {
                                            delete next[item.name];
                                          } else {
                                            next[item.name] = { ...next[item.name], qty: next[item.name].qty - 1 };
                                          }
                                          return next;
                                        });
                                      }}
                                      className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-sm font-bold"
                                    >
                                      -
                                    </button>
                                    <span className="font-bold text-sm min-w-[1.25rem] text-center">{qty}</span>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setVendorSelectedItems(prev => ({
                                          ...prev,
                                          [item.name]: { ...prev[item.name], qty: prev[item.name].qty + 1 }
                                        }));
                                      }}
                                      className="w-7 h-7 rounded-full bg-primary text-white hover:bg-primary/90 flex items-center justify-center text-sm font-bold"
                                    >
                                      +
                                    </button>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Auto-calculated total */}
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <div className="text-sm text-gray-500 mb-1">Total</div>
                      <div className="text-3xl font-bold">{formatCurrency(vendorItemsTotal)}</div>
                      {vendorItemsDescription && (
                        <div className="text-sm text-gray-500 mt-1">{vendorItemsDescription}</div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => { setVendorScannedAttendee(null); setVendorSelectedItems({}); }}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1 h-12 text-lg"
                        onClick={handleVendorCharge}
                        disabled={vendorCharging || vendorItemsTotal <= 0}
                      >
                        {vendorCharging ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <>Charge {vendorItemsTotal > 0 ? formatCurrency(vendorItemsTotal) : ''}</>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Charge Success */}
              {vendorChargeResult && (
                <Card className={`border-2 ${vendorChargeResult.success ? 'border-green-500' : 'border-red-500'}`}>
                  <CardContent className="p-6 text-center space-y-3">
                    {vendorChargeResult.success ? (
                      <>
                        <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
                        <h3 className="text-xl font-bold text-green-700">Payment Successful</h3>
                        <p className="text-3xl font-bold">{formatCurrency(vendorChargeResult.balance_after)}</p>
                        <p className="text-sm text-gray-500">New balance for {vendorChargeResult.attendee_name}</p>
                        {vendorChargeResult.low_balance && (
                          <p className="text-sm text-orange-600 bg-orange-50 p-2 rounded">Low balance warning</p>
                        )}
                      </>
                    ) : (
                      <>
                        <XCircle className="h-16 w-16 text-red-500 mx-auto" />
                        <h3 className="text-xl font-bold text-red-700">Payment Failed</h3>
                        <p className="text-gray-600">{vendorChargeResult.message}</p>
                      </>
                    )}
                    <Button
                      className="w-full h-12 text-lg mt-4"
                      onClick={() => { setVendorChargeResult(null); setVendorScannedAttendee(null); setVendorSelectedItems({}); }}
                    >
                      Scan Next
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Transactions Tab */}
          {vendorTab === 'transactions' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Transaction History</h3>
                <Button variant="ghost" size="sm" onClick={fetchVendorTransactions}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              {vendorTransactions.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-gray-400">
                    <List className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No transactions yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {vendorTransactions.map((tx) => (
                    <Card key={tx.transaction_id}>
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">{formatCurrency(tx.amount)}</div>
                            <div className="text-sm text-gray-500">{tx.items_description || 'Purchase'}</div>
                          </div>
                          <div className="text-right text-sm text-gray-400">
                            {new Date(tx.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Menu Tab */}
          {vendorTab === 'menu' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Menu Items</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setVendorMenu([...vendorMenu, { name: '', price: '', category: '', active: true }])}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Item
                </Button>
              </div>
              {vendorMenu.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-gray-400">
                    <p>No menu items configured</p>
                    <p className="text-sm mt-1">Tap "Add Item" to create your menu</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {vendorMenu.map((item, idx) => (
                    <Card key={idx} className={item.active === false ? 'opacity-50' : ''}>
                      <CardContent className="p-3 space-y-2">
                        <div className="flex gap-2 items-center">
                          <Input
                            placeholder="Item name"
                            value={item.name}
                            onChange={(e) => {
                              const items = [...vendorMenu];
                              items[idx] = {...items[idx], name: e.target.value};
                              setVendorMenu(items);
                            }}
                            className="flex-1"
                          />
                          <Input
                            type="number"
                            placeholder="Price"
                            value={item.price === '' ? '' : item.price}
                            onChange={(e) => {
                              const items = [...vendorMenu];
                              items[idx] = {...items[idx], price: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)};
                              setVendorMenu(items);
                            }}
                            className="w-24"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => {
                              const items = [...vendorMenu];
                              items[idx] = {...items[idx], active: items[idx].active === false ? true : false};
                              setVendorMenu(items);
                            }}
                            className={`text-xs px-2 py-1 rounded ${item.active === false ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700'}`}
                          >
                            {item.active === false ? 'Inactive' : 'Active'}
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setVendorMenu(vendorMenu.filter((_, i) => i !== idx))}
                            className="text-red-500 h-7 px-2"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              <Button
                className="w-full"
                onClick={async () => {
                  try {
                    const cleanedItems = vendorMenu.map(item => ({
                      ...item,
                      price: item.price === '' ? 0 : parseFloat(item.price) || 0
                    }));
                    await scannerApi.put('/vendor/menu', { menu_items: cleanedItems });
                    toast.success('Menu saved');
                    fetchVendorMenu();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to save menu');
                  }
                }}
              >
                Save Menu
              </Button>
            </div>
          )}

          {/* Summary Tab */}
          {vendorTab === 'summary' && (
            <div className="space-y-4">
              <h3 className="font-semibold mb-2">Sales Summary</h3>
              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-sm text-gray-500">Today</div>
                    <div className="text-2xl font-bold text-green-600">{formatCurrency(vendorSummary?.today?.today_sales)}</div>
                    <div className="text-xs text-gray-400">{vendorSummary?.today?.today_transactions || 0} transactions</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-sm text-gray-500">All Time</div>
                    <div className="text-2xl font-bold">{formatCurrency(vendorSummary?.all_time?.total_sales)}</div>
                    <div className="text-xs text-gray-400">{vendorSummary?.all_time?.total_transactions || 0} transactions</div>
                  </CardContent>
                </Card>
              </div>
              {vendorSummary?.all_time?.avg_transaction > 0 && (
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-sm text-gray-500">Average Transaction</div>
                    <div className="text-xl font-bold">{formatCurrency(vendorSummary.all_time.avg_transaction)}</div>
                  </CardContent>
                </Card>
              )}
              <Button variant="outline" className="w-full" onClick={() => { fetchVendorSummary(); fetchVendorTransactions(); }}>
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
            </div>
          )}
        </main>
      </div>
    );
  }

  // ============== REGULAR SCANNER RENDER ==============
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h1 className="font-bold text-lg">{scanner.dashboard_name}</h1>
            <p className="text-sm text-gray-500">{scanner.event_name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setSoundEnabled(!soundEnabled)}>
              {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="p-4 pb-24">
        {/* Mode Selector - Only show when not in a specific flow */}
        {mode === 'scan' && !scanning && (
          <div className="space-y-3 mb-4">
            {/* Big Scan QR Button */}
            <Button 
              className="w-full h-24 flex flex-col items-center justify-center bg-blue-600 hover:bg-blue-700"
              onClick={() => { setScanning(true); startScanner(); }}
              data-testid="scan-qr-btn"
            >
              <Camera className="h-10 w-10 mb-2" />
              <span className="text-xl font-semibold">Scan QR</span>
            </Button>
            
            {/* Second row - smaller buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button 
                className="h-16 flex flex-col items-center justify-center bg-orange-600 hover:bg-orange-700 text-sm"
                onClick={() => setMode('onspot')}
                data-testid="onspot-reg-btn"
              >
                <UserPlus className="h-5 w-5 mb-1" />
                <span className="font-semibold">On-Spot Reg.</span>
              </Button>
              <Button 
                className="h-16 flex flex-col items-center justify-center bg-purple-600 hover:bg-purple-700 text-sm"
                onClick={() => setMode('manual')}
                data-testid="manual-checkin-btn"
              >
                <Search className="h-5 w-5 mb-1" />
                <span className="font-semibold">Manual Check-in</span>
              </Button>
            </div>
          </div>
        )}

        {/* On-Spot Registration Mode */}
        {mode === 'onspot' && (
          <div className="space-y-4">
            <Button variant="ghost" onClick={handleBackToModeSelect} className="mb-2">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            
            {/* Success Screen */}
            {onspotSuccess ? (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-6 text-center">
                  <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-green-800 mb-2">Registration Successful!</h2>
                  <p className="text-green-700 mb-4">{onspotSuccess.name}</p>
                  <div className="bg-white rounded-lg p-4 mb-4 text-left">
                    <p><strong>Ticket ID:</strong> {onspotSuccess.ticket_id}</p>
                    <p><strong>Category:</strong> {onspotSuccess.category}</p>
                    <p><strong>Badge Code:</strong> {onspotSuccess.registration_code}</p>
                    {onspotSuccess.send_results?.email?.success && (
                      <p className="text-green-600 mt-2">✓ Email sent</p>
                    )}
                    {onspotSuccess.send_results?.whatsapp?.success && (
                      <p className="text-green-600">✓ WhatsApp sent</p>
                    )}
                  </div>
                  <p className="text-sm text-green-600 mb-4">
                    Write the name and category on the blank badge, then hand it over.
                  </p>
                  <Button onClick={() => setOnspotSuccess(null)} className="w-full">
                    Register Next
                  </Button>
                </CardContent>
              </Card>
            ) : onspotBadge ? (
              /* Registration Form */
              <Card>
                <CardContent className="p-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                    <p className="font-mono font-bold text-lg text-orange-800">{onspotBadge.registration_code}</p>
                    <p className="text-sm text-orange-600">Badge ready for activation</p>
                  </div>
                  
                  <form onSubmit={handleOnspotSubmit} className="space-y-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        value={onspotForm.name}
                        onChange={(e) => setOnspotForm({...onspotForm, name: e.target.value})}
                        placeholder="Enter full name"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label>Call Name</Label>
                      <Input
                        value={onspotForm.call_name}
                        onChange={(e) => setOnspotForm({...onspotForm, call_name: e.target.value})}
                        placeholder="Nickname or first name"
                      />
                    </div>
                    
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={onspotForm.email}
                        onChange={(e) => setOnspotForm({...onspotForm, email: e.target.value})}
                        placeholder="email@example.com"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label>Code</Label>
                        <Select 
                          value={onspotForm.phone_country_code} 
                          onValueChange={(v) => setOnspotForm({...onspotForm, phone_country_code: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="+91">+91</SelectItem>
                            <SelectItem value="+1">+1</SelectItem>
                            <SelectItem value="+44">+44</SelectItem>
                            <SelectItem value="+971">+971</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Phone *</Label>
                        <Input
                          type="tel"
                          value={onspotForm.phone_number}
                          onChange={(e) => {
                            // Only allow numbers, max 10 digits
                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setOnspotForm({...onspotForm, phone_number: value});
                          }}
                          maxLength={10}
                          placeholder="10 digit number"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Category *</Label>
                      <Select value={onspotForm.category_id} onValueChange={handleCategoryChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {onspotCategories.map(cat => (
                            <SelectItem key={cat.category_id} value={cat.category_id}>
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: cat.category_color }}
                                />
                                {cat.category_name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Gender</Label>
                        <Select 
                          value={onspotForm.gender} 
                          onValueChange={(v) => setOnspotForm({...onspotForm, gender: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>T-Shirt Size</Label>
                        <Select 
                          value={onspotForm.tshirt_size} 
                          onValueChange={(v) => setOnspotForm({...onspotForm, tshirt_size: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="XS">XS</SelectItem>
                            <SelectItem value="S">S</SelectItem>
                            <SelectItem value="M">M</SelectItem>
                            <SelectItem value="L">L</SelectItem>
                            <SelectItem value="XL">XL</SelectItem>
                            <SelectItem value="XXL">XXL</SelectItem>
                            <SelectItem value="XXXL">XXXL</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Club / Organization</Label>
                      <Input
                        value={onspotForm.club_name}
                        onChange={(e) => setOnspotForm({...onspotForm, club_name: e.target.value})}
                        placeholder="Organization name"
                      />
                    </div>
                    
                    <div>
                      <Label>Dietary Preference</Label>
                      <Select 
                        value={onspotForm.dietary_preference} 
                        onValueChange={(v) => setOnspotForm({...onspotForm, dietary_preference: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vegetarian">Vegetarian</SelectItem>
                          <SelectItem value="non-vegetarian">Non-Vegetarian</SelectItem>
                          <SelectItem value="vegan">Vegan</SelectItem>
                          <SelectItem value="jain">Jain</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Scanner Permissions - Auto-filled from category */}
                    {onspotForm.category_id && Object.keys(onspotForm.custom_limits).length > 0 && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <Label className="text-sm font-medium mb-2 block">Scanner Permissions</Label>
                        <div className="text-xs text-gray-500 space-y-1">
                          {Object.entries(onspotForm.custom_limits).map(([key, value]) => {
                            const dashboard = onspotDashboards.find(d => d.dashboard_id === key);
                            return (
                              <div key={key} className="flex justify-between">
                                <span>{dashboard?.dashboard_name || key}</span>
                                <span className="font-medium">{value === -1 ? 'Unlimited' : value}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    <Button 
                      type="submit" 
                      className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                      disabled={onspotSubmitting}
                    >
                      {onspotSubmitting ? (
                        <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Registering...</>
                      ) : (
                        <><CheckCircle className="h-5 w-5 mr-2" /> Register & Send Badge</>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            ) : (
              /* Badge Lookup */
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-4">On-Spot Registration</h2>
                  <p className="text-gray-500 mb-4">Enter the registration code from the blank badge</p>
                  
                  <div className="flex gap-2 mb-4">
                    <Input
                      value={onspotCode}
                      onChange={(e) => setOnspotCode(e.target.value.toUpperCase())}
                      placeholder="e.g., REG-001"
                      className="font-mono text-lg"
                      onKeyDown={(e) => e.key === 'Enter' && handleLookupBadge()}
                    />
                    <Button onClick={handleLookupBadge} disabled={onspotLoading}>
                      {onspotLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                    </Button>
                  </div>
                  
                  <div className="text-center text-gray-400 text-sm">
                    <p>Find the code printed on the blank badge</p>
                    <p>(e.g., REG-001, REG-002, etc.)</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Manual Check-in Mode */}
        {mode === 'manual' && (
          <div className="space-y-4">
            <Button variant="ghost" onClick={handleBackToModeSelect} className="mb-2">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            
            {/* Selected Attendee Confirmation */}
            {manualSelectedAttendee ? (
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                    <User className="h-10 w-10 text-purple-600" />
                  </div>
                  <h2 className="text-2xl font-bold mb-1">{manualSelectedAttendee.name}</h2>
                  <p className="text-gray-500 mb-2">Ticket: {manualSelectedAttendee.ticket_id}</p>
                  {manualSelectedAttendee.category_name && (
                    <span 
                      className="inline-block px-4 py-1 rounded-full text-white text-sm mb-4"
                      style={{ backgroundColor: manualSelectedAttendee.category_color || '#3B82F6' }}
                    >
                      {manualSelectedAttendee.category_name}
                    </span>
                  )}
                  
                  {manualSelectedAttendee.phone_number && (
                    <p className="text-sm text-gray-500 flex items-center justify-center gap-2 mb-1">
                      <Phone className="h-4 w-4" /> {manualSelectedAttendee.phone_number}
                    </p>
                  )}
                  {manualSelectedAttendee.email && (
                    <p className="text-sm text-gray-500 flex items-center justify-center gap-2 mb-4">
                      <Mail className="h-4 w-4" /> {manualSelectedAttendee.email}
                    </p>
                  )}
                  
                  <div className="flex gap-3 mt-4">
                    <Button 
                      variant="outline" 
                      onClick={handleManualCancelSelection}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleManualConfirmCheckin}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      disabled={manualConfirming}
                    >
                      {manualConfirming ? (
                        <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Confirming...</>
                      ) : (
                        <><CheckCircle className="h-5 w-5 mr-2" /> Confirm Check-in</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Search Form */
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-2">Manual Check-in</h2>
                  <p className="text-gray-500 mb-4">Search by Ticket ID or Name</p>
                  
                  <div className="flex gap-2 mb-4">
                    <Input
                      value={manualSearchQuery}
                      onChange={(e) => setManualSearchQuery(e.target.value)}
                      placeholder="e.g., S-0001 or John"
                      className="text-lg"
                      onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
                      data-testid="manual-search-input"
                    />
                    <Button onClick={handleManualSearch} disabled={manualSearching}>
                      {manualSearching ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                    </Button>
                  </div>
                  
                  {/* Search Results */}
                  {manualSearchResults.length > 0 ? (
                    <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
                      {manualSearchResults.map((attendee) => (
                        <div 
                          key={attendee.attendee_id}
                          className="p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                          onClick={() => handleManualSelectAttendee(attendee)}
                          data-testid={`search-result-${attendee.ticket_id}`}
                        >
                          <div>
                            <p className="font-semibold">{attendee.name}</p>
                            <p className="text-sm text-gray-500">{attendee.ticket_id}</p>
                          </div>
                          <div className="text-right">
                            {attendee.category_name && (
                              <span 
                                className="inline-block px-2 py-0.5 rounded-full text-white text-xs"
                                style={{ backgroundColor: attendee.category_color || '#3B82F6' }}
                              >
                                {attendee.category_name}
                              </span>
                            )}
                            {attendee.check_in_status === 'checked_in' && (
                              <p className="text-xs text-yellow-600 mt-1">Already checked in</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : manualSearchQuery && !manualSearching ? (
                    <div className="text-center text-gray-400 py-8">
                      <Search className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p>No results found</p>
                      <p className="text-sm">Try a different search term</p>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400 py-8">
                      <User className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p>Enter Ticket ID or Name</p>
                      <p className="text-sm">Results will appear here</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Scan Mode - Stats */}
        {(mode === 'scan' && scanning) && (
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{stats?.today_scans || 0}</p>
                  <p className="text-sm text-gray-500">Today</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{stats?.total_scans || 0}</p>
                  <p className="text-sm text-gray-500">Total</p>
                </CardContent>
              </Card>
            </div>

        {/* Scanner Area */}
        <Card className="mb-4 overflow-hidden">
          <CardContent className="p-0">
            <div className="relative bg-black" style={{ minHeight: '350px' }}>
              {/* Video Element */}
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                style={{ 
                  display: scanning ? 'block' : 'none',
                  minHeight: '350px'
                }}
                playsInline
                autoPlay
                muted
              />
              
              {/* Scanning overlay */}
              {scanning && (
                <div className="absolute inset-0 pointer-events-none">
                  {/* Scan frame */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-64 h-64 relative">
                      {/* Corner brackets */}
                      <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-green-400"></div>
                      <div className="absolute top-0 right-0 w-12 h-12 border-t-4 border-r-4 border-green-400"></div>
                      <div className="absolute bottom-0 left-0 w-12 h-12 border-b-4 border-l-4 border-green-400"></div>
                      <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-green-400"></div>
                      
                      {/* Scanning line animation */}
                      <div className="absolute inset-x-0 h-0.5 bg-green-400 animate-pulse" 
                           style={{ top: '50%', boxShadow: '0 0 10px #4ade80' }}></div>
                    </div>
                  </div>
                  
                  {/* Status text */}
                  <div className="absolute bottom-4 left-0 right-0 text-center">
                    <p className="text-white text-sm bg-black/50 inline-block px-4 py-2 rounded-full">
                      {processing ? '⏳ Processing...' : '📷 Point camera at QR code'}
                    </p>
                    {debugInfo && (
                      <p className="text-white/70 text-xs bg-black/40 inline-block px-3 py-1 rounded mt-2">
                        {debugInfo}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Placeholder when not scanning */}
              {!scanning && (
                <div className="w-full flex flex-col items-center justify-center bg-gray-900 text-white p-8" style={{ minHeight: '350px' }}>
                  {cameraError ? (
                    <>
                      <XCircle className="h-16 w-16 mb-4 text-red-400" />
                      <p className="text-lg text-center text-red-300 mb-2">Camera Error</p>
                      <p className="text-sm text-gray-400 text-center mb-4">{cameraError}</p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.location.reload()}
                        className="text-white border-white hover:bg-white/20"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" /> Refresh Page
                      </Button>
                    </>
                  ) : (
                    <>
                      <Camera className="h-16 w-16 mb-4 opacity-50" />
                      <p className="text-lg">Ready to Scan</p>
                      <p className="text-sm text-gray-400 text-center">Tap Start Scanner to begin</p>
                    </>
                  )}
                </div>
              )}
              
              {/* Processing overlay */}
              {processing && scanning && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <div className="text-white text-center">
                    <Loader2 className="h-12 w-12 animate-spin mx-auto mb-2" />
                    <p>Processing QR Code...</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 flex justify-center bg-white">
              <Button
                onClick={scanning ? stopScanner : startScanner}
                className={`w-full max-w-xs h-14 text-lg ${scanning ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'}`}
                size="lg"
                disabled={processing}
              >
                {scanning ? (
                  <><CameraOff className="mr-2 h-6 w-6" /> Stop Scanner</>
                ) : (
                  <><Camera className="mr-2 h-6 w-6" /> Start Scanner</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Today's Scans */}
        <Button variant="outline" className="w-full mb-4" onClick={() => setShowScans(!showScans)}>
          <List className="mr-2 h-4 w-4" />
          {showScans ? 'Hide' : 'Show'} Today&apos;s Scans ({todayScans.length})
        </Button>

        {showScans && (
          <Card>
            <CardContent className="p-0">
              {todayScans.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No scans yet today</div>
              ) : (
                <div className="divide-y max-h-96 overflow-y-auto">
                  {todayScans.map((scan, idx) => (
                    <div key={idx} className="p-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{scan.attendee_name}</p>
                        <p className="text-sm text-gray-500">{scan.ticket_id}</p>
                      </div>
                      <p className="text-sm text-gray-400">{formatToLocalTimeOnly(scan.scanned_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        </>
        )}
        {/* End of Scan Mode */}
      </main>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm mx-4" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Confirm Check-in</DialogTitle>
          </DialogHeader>
          
          {loadingAttendee ? (
            <div className="py-8 text-center">
              <Loader2 className="h-10 w-10 animate-spin mx-auto mb-3 text-blue-500" />
              <p className="text-gray-500">Loading...</p>
            </div>
          ) : pendingAttendee?.valid !== false ? (
            <div className="py-4 text-center">
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <User className="h-10 w-10 text-blue-600" />
              </div>
              <p className="text-2xl font-bold">{pendingAttendee?.name || 'Unknown'}</p>
              <p className="text-gray-500 mt-1">Ticket: {pendingAttendee?.ticket_id}</p>
              {pendingAttendee?.category && (
                <span 
                  className="inline-block px-4 py-1 rounded-full text-white text-sm mt-3"
                  style={{ backgroundColor: pendingAttendee?.category_color || '#3B82F6' }}
                >
                  {pendingAttendee?.category}
                </span>
              )}
              {pendingAttendee?.already_scanned && (
                <p className="text-yellow-600 font-medium mt-3 text-lg">
                  ⚠️ Already scanned {pendingAttendee?.scan_count || 1}x
                </p>
              )}
            </div>
          ) : (
            <div className="py-6 text-center">
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-3" />
              <p className="text-red-600 font-medium text-lg">{pendingAttendee?.message || 'Invalid QR Code'}</p>
            </div>
          )}
          
          <DialogFooter className="flex gap-3 mt-4">
            <Button variant="outline" onClick={handleCancelScan} className="flex-1 h-12">
              Cancel
            </Button>
            {pendingAttendee?.valid !== false && (
              <Button onClick={handleConfirmScan} className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-lg">
                ✓ Confirm
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Dialog - Enhanced Celebratory Success Screen */}
      <Dialog open={showResultDialog} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm mx-4 p-0 overflow-hidden" onPointerDownOutside={(e) => e.preventDefault()}>
          {/* Success State */}
          {lastScan?.success ? (
            <div className="relative">
              <Confetti active={lastScan?.success} />
              <div className="bg-gradient-to-br from-green-500 via-green-600 to-emerald-600 text-white p-6 text-center relative overflow-hidden">
                {/* Decorative circles */}
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full"></div>
                <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full"></div>
                
                {/* Success icon with pulse animation */}
                <div className="relative z-10">
                  <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <CheckCircle className="h-14 w-14 text-white" />
                  </div>
                  
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Sparkles className="h-6 w-6 text-yellow-300" />
                    <h2 className="text-3xl font-bold">Welcome!</h2>
                    <Sparkles className="h-6 w-6 text-yellow-300" />
                  </div>
                  
                  {/* Attendee Name - Large and prominent */}
                  <p className="text-2xl font-bold mb-1">{lastScan?.attendee_info?.name}</p>
                  <p className="text-sm opacity-80">{lastScan?.message}</p>
                </div>
              </div>
              
              {/* Attendee Details Section */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white p-5">
                <div className="space-y-1 divide-y divide-white/10">
                  {/* Registration Type (Category) - Prominent badge */}
                  {lastScan?.attendee_info?.category && (
                    <div className="flex items-center gap-3 py-2">
                      <Tag className="h-5 w-5 opacity-70 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs opacity-70 uppercase tracking-wide">Registration Type</p>
                        <span 
                          className="inline-block px-3 py-1 rounded-full text-sm font-semibold mt-1"
                          style={{ 
                            backgroundColor: lastScan?.attendee_info?.category_color || '#3B82F6',
                            color: 'white'
                          }}
                        >
                          {lastScan?.attendee_info?.category}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <DetailRow 
                    icon={UserCircle} 
                    label="Gender" 
                    value={lastScan?.attendee_info?.gender}
                  />
                  
                  <DetailRow 
                    icon={Shirt} 
                    label="T-Shirt Size" 
                    value={lastScan?.attendee_info?.tshirt_size}
                  />
                  
                  <DetailRow 
                    icon={Ticket} 
                    label="Ticket ID" 
                    value={lastScan?.attendee_info?.ticket_id}
                  />
                  
                  {lastScan?.attendee_info?.club_name && (
                    <DetailRow 
                      icon={User} 
                      label="Club/Organization" 
                      value={lastScan?.attendee_info?.club_name}
                    />
                  )}
                </div>
              </div>
              
              <div className="p-4 bg-white">
                <Button 
                  onClick={handleResultAcknowledged} 
                  className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                  data-testid="scan-next-btn"
                >
                  ✓ OK - Scan Next
                </Button>
              </div>
            </div>
          ) : (
            /* Error/Duplicate State */
            <div>
              <div className={`p-8 ${getResultColor()} text-white text-center`}>
                <div className="flex justify-center mb-4">{getResultIcon()}</div>
                <h2 className="text-3xl font-bold mb-2">
                  {lastScan?.status === 'duplicate' ? 'Duplicate' : 'Failed'}
                </h2>
                <p className="text-lg opacity-90">{lastScan?.message}</p>
                {lastScan?.attendee_info && (
                  <div className="bg-white/20 rounded-lg p-4 mt-4 text-left">
                    <p className="font-bold text-xl">{lastScan.attendee_info.name}</p>
                    <p className="opacity-90">{lastScan.attendee_info.ticket_id}</p>
                  </div>
                )}
              </div>
              <div className="p-4 bg-white">
                <Button 
                  onClick={handleResultAcknowledged} 
                  className="w-full h-14 text-lg"
                  data-testid="scan-next-btn"
                >
                  OK - Scan Next
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScannerDashboard;
